import React, { useState, useMemo } from "react";

// ─── Brand tokens ────────────────────────────────────────────────────────────
const c = {
  gold: "#C9973A",
  goldLight: "#E8C06A",
  goldDark: "#8B6820",
  bg: "#080806",
  card: "#0E0E0A",
  cardAlt: "#111108",
  border: "#2A2410",
  borderStrong: "#4A3A18",
  text: "#F0E0C8",
  soft: "#CFC3A0",
  muted: "#8A7A50",
  muted2: "#5C5030",
  success: "#4DAA57",
  danger: "#C75B5B",
};

// ─── Copy (EN + ES) ──────────────────────────────────────────────────────────
const copy = {
  en: {
    brand: "KY Empowered Fitness",
    title: "Weekly Check-In",
    tagline: "A quick check-in so we can see what happened this week and make the right next move.",
    systemLine: "This is how the KY system works: we observe, adjust, and keep you moving forward.",
    timeNote: "Takes 2–3 minutes",
    stepOf: (s: number, t: number) => `Step ${s} of ${t}`,
    barNote: "Quick enough to finish now. Useful enough to matter.",
    back: "Back",
    next: "Next",
    review: "Review",
    submit: "Submit Check-In",
    optional: "Optional",
    required: "Required",
    successTitle: "Check-In Received",
    successBody: "Your week is in. We'll use this to make the next move based on what actually happened — not guesswork.",
    successSub: "Kevin & Yuny will review your check-in and adjust your plan accordingly.",
    signed: "— Kevin & Yuny",
    reassurance: "You don't need a perfect week to make progress. You just need an honest one.",
    sections: [
      { title: "Body Metrics", desc: "Quick data. Clean trend lines." },
      { title: "Execution", desc: "How closely the week matched the plan." },
      { title: "Recovery", desc: "This tells us whether the plan is sustainable." },
      { title: "Training & Weekend", desc: "Where performance and real life meet." },
      { title: "Reflection", desc: "Signal from you — not just the numbers." },
      { title: "Review", desc: "Here's the week you're about to hand to your coaches." },
    ],
    f: {
      weight: "Average weekly weight",
      weightHelper: "Use your average for the week, not one random weigh-in.",
      unit: "Unit",
      lbs: "lbs",
      kg: "kg",
      adherence: "Adherence percentage",
      adherenceHelper: "Estimate the % of meals you followed as planned.",
      protein: "Protein consistency",
      proteinLow: "Missed most days",
      proteinHigh: "Hit it every day",
      energy: "Energy",
      energyLow: "Drained",
      energyHigh: "Strong all week",
      hunger: "Hunger",
      hungerLow: "Barely hungry",
      hungerHigh: "Constantly hungry",
      sleep: "Sleep quality",
      sleepLow: "Poor sleep",
      sleepHigh: "Slept great",
      digestion: "Digestion",
      good: "Good",
      neutral: "Neutral",
      poor: "Poor",
      training: "Training performance",
      improving: "Improving",
      stable: "Stable",
      declining: "Declining",
      weekendQ: "Did you stay on track this weekend?",
      weekendHelper: "The weekend usually tells the truth about the system.",
      weekendDetail: "What happened?",
      weekendDetailHelper: "A quick note is enough. We're looking for context, not a confession.",
      weekendPlaceholder: "e.g. Dinner out Saturday turned into two unplanned meals.",
      yes: "Yes",
      no: "No",
      struggle: "Biggest struggle this week",
      strugglePlaceholder: "What made the week harder than it needed to be?",
      win: "Biggest win this week",
      winHelper: "Even a small win counts. We're looking for signal, not perfection.",
      winPlaceholder: "What went right, even if the week wasn't perfect?",
    },
    reviewLabels: {
      weight: "Weight", adherence: "Adherence", protein: "Protein",
      energy: "Energy", hunger: "Hunger", sleep: "Sleep", digestion: "Digestion",
      training: "Training", weekend: "Weekend", weekendDetail: "Weekend note",
      struggle: "Struggle", win: "Win",
    },
  },
  es: {
    brand: "KY Empowered Fitness",
    title: "Chequeo Semanal",
    tagline: "Un chequeo rápido para ver qué pasó esta semana y tomar la siguiente decisión correcta.",
    systemLine: "Así funciona el sistema KY: observamos, ajustamos y te mantenemos avanzando.",
    timeNote: "Toma 2–3 minutos",
    stepOf: (s: number, t: number) => `Paso ${s} de ${t}`,
    barNote: "Suficientemente rápido para completar ahora. Suficientemente útil para importar.",
    back: "Atrás",
    next: "Siguiente",
    review: "Revisar",
    submit: "Enviar chequeo",
    optional: "Opcional",
    required: "Requerido",
    successTitle: "Chequeo Recibido",
    successBody: "Tu semana ya entró. Usaremos esto para hacer el próximo ajuste basado en lo que realmente pasó — sin suposiciones.",
    successSub: "Kevin y Yuny revisarán tu chequeo y ajustarán tu plan en consecuencia.",
    signed: "— Kevin & Yuny",
    reassurance: "No necesitas una semana perfecta para progresar. Solo necesitas una honesta.",
    sections: [
      { title: "Datos Corporales", desc: "Datos rápidos. Tendencias limpias." },
      { title: "Ejecución", desc: "Qué tan cerca estuvo la semana del plan." },
      { title: "Recuperación", desc: "Esto nos dice si el plan es sostenible." },
      { title: "Entrenamiento y Fin de Semana", desc: "Donde el rendimiento y la vida real se encuentran." },
      { title: "Reflexión", desc: "Señal tuya — no solo números." },
      { title: "Revisión", desc: "Aquí está la semana que estás a punto de entregar a tus coaches." },
    ],
    f: {
      weight: "Peso promedio semanal",
      weightHelper: "Usa tu promedio semanal, no un peso aislado.",
      unit: "Unidad",
      lbs: "lb",
      kg: "kg",
      adherence: "Porcentaje de adherencia",
      adherenceHelper: "Estima el % de comidas que seguiste según el plan.",
      protein: "Consistencia con proteína",
      proteinLow: "Fallé la mayoría",
      proteinHigh: "Cumplí todos los días",
      energy: "Energía",
      energyLow: "Sin energía",
      energyHigh: "Fuerte toda la semana",
      hunger: "Hambre",
      hungerLow: "Casi sin hambre",
      hungerHigh: "Hambre constante",
      sleep: "Calidad del sueño",
      sleepLow: "Mal sueño",
      sleepHigh: "Dormí muy bien",
      digestion: "Digestión",
      good: "Buena",
      neutral: "Neutral",
      poor: "Mala",
      training: "Rendimiento en entrenamiento",
      improving: "Mejorando",
      stable: "Estable",
      declining: "Empeorando",
      weekendQ: "¿Te mantuviste en el plan este fin de semana?",
      weekendHelper: "El fin de semana generalmente dice la verdad sobre el sistema.",
      weekendDetail: "¿Qué pasó?",
      weekendDetailHelper: "Una nota rápida es suficiente. Buscamos contexto, no una confesión.",
      weekendPlaceholder: "Ej. Una salida a cenar el sábado terminó en dos comidas no planificadas.",
      yes: "Sí",
      no: "No",
      struggle: "Mayor dificultad de esta semana",
      strugglePlaceholder: "¿Qué hizo la semana más difícil de lo necesario?",
      win: "Mayor victoria de esta semana",
      winHelper: "Hasta un logro pequeño cuenta. Buscamos señales, no perfección.",
      winPlaceholder: "¿Qué salió bien, aunque la semana no fuera perfecta?",
    },
    reviewLabels: {
      weight: "Peso", adherence: "Adherencia", protein: "Proteína",
      energy: "Energía", hunger: "Hambre", sleep: "Sueño", digestion: "Digestión",
      training: "Entrenamiento", weekend: "Fin de semana", weekendDetail: "Nota del fin de semana",
      struggle: "Dificultad", win: "Victoria",
    },
  },
} as const;

type Lang = "en" | "es";
type CopyType = typeof copy[Lang];

// ─── Engine payload type (KY Weekly Check-In Data Contract v1) ───────────────
type CheckInPayload = {
  form_type: "ky_weekly_checkin";
  submitted_at: string;
  language: Lang;
  body_metrics: {
    avg_weekly_weight: number;
    unit: "lbs" | "kg";
  };
  execution: {
    adherence_percent: number;       // integer 0–100
    protein_consistency: number;     // integer 1–10
  };
  recovery: {
    energy: number;                  // integer 1–10
    hunger: number;                  // integer 1–10
    sleep_quality: number;           // integer 1–10
    digestion: "good" | "neutral" | "poor";
  };
  training: {
    training_performance: "improving" | "stable" | "declining";
    weekend_compliance: "yes" | "no";
    weekend_detail: string | null;
  };
  reflection: {
    biggest_struggle: string;
    biggest_win: string;
  };
};

const TOTAL_STEPS = 6;
const FORMSPREE_ENDPOINT = "https://formspree.io/f/YOUR_CHECKIN_ENDPOINT"; // ← replace with Formspree form ID

const initialForm = {
  avgWeeklyWeight: "",
  unit: "lbs",
  adherencePercent: "",
  proteinConsistency: 7,
  energy: 7,
  hunger: 5,
  digestion: "neutral",
  sleepQuality: 7,
  trainingPerformance: "stable",
  weekendCompliance: "",
  weekendDetail: "",
  biggestStruggle: "",
  biggestWin: "",
};

// ─── Shared styles ────────────────────────────────────────────────────────────
const inputBase: React.CSSProperties = {
  width: "100%",
  background: "#0a0a07",
  border: `1px solid ${c.border}`,
  borderRadius: 10,
  color: c.text,
  fontFamily: "Georgia, serif",
  fontSize: 15,
  padding: "13px 14px",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.2s",
};

const radioOption = (selected: boolean): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "11px 14px",
  borderRadius: 9,
  border: `1px solid ${selected ? c.goldDark : c.border}`,
  background: selected ? `${c.goldDark}18` : "transparent",
  cursor: "pointer",
  transition: "all 0.18s",
});

// ─── Sub-components ───────────────────────────────────────────────────────────
function SectionHeader({ title, desc, icon }: { title: string; desc: string; icon?: string }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        {icon && <span style={{ fontSize: 18 }}>{icon}</span>}
        <h2 style={{ margin: 0, color: c.text, fontSize: 22, fontWeight: 600, letterSpacing: 0.3 }}>{title}</h2>
      </div>
      <p style={{ margin: 0, color: c.muted, fontSize: 13, lineHeight: 1.6 }}>{desc}</p>
    </div>
  );
}

function SliderField({ label, value, onChange, lowLabel, highLabel, helper }: {
  label: string; value: number; onChange: (v: number) => void;
  lowLabel: string; highLabel: string; helper?: string;
}) {
  return (
    <div style={{ background: "#0a0a07", border: `1px solid ${c.border}`, borderRadius: 12, padding: 18, marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <label style={{ color: c.soft, fontSize: 13, letterSpacing: 0.5 }}>{label}</label>
        <span style={{ color: c.gold, fontSize: 16, fontWeight: 700, background: `${c.goldDark}22`, padding: "3px 10px", borderRadius: 20, border: `1px solid ${c.goldDark}44` }}>{value}/10</span>
      </div>
      <input type="range" min={1} max={10} value={value} onChange={(e) => onChange(Number(e.target.value))} style={{ width: "100%", accentColor: c.gold, cursor: "pointer" }} />
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
        <span style={{ color: c.muted2, fontSize: 11 }}>{lowLabel}</span>
        <span style={{ color: c.muted2, fontSize: 11 }}>{highLabel}</span>
      </div>
      {helper && <p style={{ margin: "10px 0 0", color: c.muted2, fontSize: 11, lineHeight: 1.6 }}>{helper}</p>}
    </div>
  );
}

function RadioGroup({ options, value, onChange }: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {options.map((opt) => (
        <div key={opt.value} style={radioOption(value === opt.value)} onClick={() => onChange(opt.value)}>
          <div style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${value === opt.value ? c.gold : c.muted2}`, background: value === opt.value ? c.gold : "transparent", flexShrink: 0, transition: "all 0.18s", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {value === opt.value && <div style={{ width: 6, height: 6, borderRadius: "50%", background: c.bg }} />}
          </div>
          <span style={{ color: value === opt.value ? c.text : c.muted, fontSize: 14, transition: "color 0.18s" }}>{opt.label}</span>
        </div>
      ))}
    </div>
  );
}

function FieldLabel({ children, optional, t }: { children: React.ReactNode; optional?: boolean; t: { optional: string } }) {
  return (
    <label style={{ display: "block", color: c.soft, fontSize: 12, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8, fontWeight: 600 }}>
      {children}
      {optional && <span style={{ color: c.muted2, marginLeft: 8, fontSize: 10, textTransform: "none", letterSpacing: 0 }}>({t.optional})</span>}
    </label>
  );
}

function HelperText({ children }: { children: React.ReactNode }) {
  return <p style={{ margin: "7px 0 0", color: c.muted2, fontSize: 11, lineHeight: 1.6 }}>{children}</p>;
}

function ReviewCard({ icon, title, rows }: { icon: string; title: string; rows: { label: string; value: string }[] }) {
  return (
    <div style={{ background: "#0a0a07", border: `1px solid ${c.border}`, borderRadius: 12, overflow: "hidden" }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${c.border}`, display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 16 }}>{icon}</span>
        <span style={{ color: c.gold, fontSize: 12, letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 700 }}>{title}</span>
      </div>
      <div style={{ padding: "4px 0" }}>
        {rows.map(({ label, value }) => (
          <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "10px 16px", borderBottom: `1px solid ${c.border}88` }}>
            <span style={{ color: c.muted, fontSize: 13, flexShrink: 0, marginRight: 12 }}>{label}</span>
            <span style={{ color: c.text, fontSize: 13, textAlign: "right", maxWidth: "58%" }}>{value || "—"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function KYWeeklyCheckIn() {
  const [lang, setLang] = useState<Lang>("en");
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [fadeKey, setFadeKey] = useState(0);

  const t: CopyType = copy[lang];
  const f = t.f;
  const progress = (step / TOTAL_STEPS) * 100;

  const update = (key: string, val: unknown) => setForm((prev) => ({ ...prev, [key]: val }));

  const isValid: Record<number, boolean> = useMemo(() => ({
    1: !!form.avgWeeklyWeight.trim(),
    2: form.adherencePercent !== "",
    3: !!form.digestion,
    4: !!form.trainingPerformance && !!form.weekendCompliance,
    5: !!form.biggestStruggle.trim() && !!form.biggestWin.trim(),
    6: true,
  }), [form]);

  const goNext = () => {
    if (step < TOTAL_STEPS && isValid[step]) { setFadeKey((k) => k + 1); setStep((s) => s + 1); }
  };
  const goBack = () => {
    if (step > 1) { setFadeKey((k) => k + 1); setStep((s) => s - 1); }
  };

  // ── Payload builder — Section 4 normalization applied ─────────────────────
  const buildPayload = (): CheckInPayload => ({
    form_type: "ky_weekly_checkin",
    submitted_at: new Date().toISOString(),
    language: lang,
    body_metrics: {
      avg_weekly_weight: parseFloat(form.avgWeeklyWeight) || 0,
      unit: form.unit as "lbs" | "kg",
    },
    execution: {
      adherence_percent: Math.min(100, Math.max(0, parseInt(form.adherencePercent as string, 10) || 0)),
      protein_consistency: Math.min(10, Math.max(1, Math.round(form.proteinConsistency))),
    },
    recovery: {
      energy:       Math.min(10, Math.max(1, Math.round(form.energy))),
      hunger:       Math.min(10, Math.max(1, Math.round(form.hunger))),
      sleep_quality: Math.min(10, Math.max(1, Math.round(form.sleepQuality))),
      digestion: form.digestion as "good" | "neutral" | "poor",
    },
    training: {
      training_performance: form.trainingPerformance as "improving" | "stable" | "declining",
      weekend_compliance: form.weekendCompliance as "yes" | "no",
      weekend_detail: form.weekendDetail.trim() || null,
    },
    reflection: {
      biggest_struggle: form.biggestStruggle.trim(),
      biggest_win: form.biggestWin.trim(),
    },
  });

  // ── Contract validator — Section 5 validation rules ───────────────────────
  // Returns true only if checkin_ready_for_engine.
  // A failed check blocks submission and prevents bad data entering Modules 6/7.
  const validatePayload = (p: CheckInPayload): boolean => {
    if (p.form_type !== "ky_weekly_checkin") return false;
    if (!p.submitted_at) return false;
    if (!["en", "es"].includes(p.language)) return false;
    if (!p.body_metrics.avg_weekly_weight || p.body_metrics.avg_weekly_weight <= 0) return false;
    if (!["lbs", "kg"].includes(p.body_metrics.unit)) return false;
    if (p.execution.adherence_percent < 0 || p.execution.adherence_percent > 100) return false;
    if (!Number.isInteger(p.execution.adherence_percent)) return false;
    if (p.execution.protein_consistency < 1 || p.execution.protein_consistency > 10) return false;
    if (!Number.isInteger(p.execution.protein_consistency)) return false;
    if (p.recovery.energy < 1 || p.recovery.energy > 10) return false;
    if (!Number.isInteger(p.recovery.energy)) return false;
    if (p.recovery.hunger < 1 || p.recovery.hunger > 10) return false;
    if (!Number.isInteger(p.recovery.hunger)) return false;
    if (p.recovery.sleep_quality < 1 || p.recovery.sleep_quality > 10) return false;
    if (!Number.isInteger(p.recovery.sleep_quality)) return false;
    if (!["good", "neutral", "poor"].includes(p.recovery.digestion)) return false;
    if (!["improving", "stable", "declining"].includes(p.training.training_performance)) return false;
    if (!["yes", "no"].includes(p.training.weekend_compliance)) return false;
    if (!p.reflection.biggest_struggle.trim()) return false;
    if (!p.reflection.biggest_win.trim()) return false;
    return true;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setStatus("sending");
    const payload = buildPayload();

    // Contract gate — do not proceed if payload is invalid
    if (!validatePayload(payload)) {
      console.error("KY Check-In: payload failed contract validation", payload);
      setStatus("error");
      return;
    }

    console.log("KY Check-In Payload (contract-valid):", JSON.stringify(payload, null, 2));

    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("failed");
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  // ── Success screen ────────────────────────────────────────────────────────
  if (status === "success") {
    return (
      <div style={{ minHeight: "100vh", background: c.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 20px", fontFamily: "Georgia, serif" }}>
        <div style={{ maxWidth: 520, width: "100%", textAlign: "center" }}>
          <div style={{ width: 64, height: 64, borderRadius: "50%", background: `${c.goldDark}33`, border: `2px solid ${c.goldDark}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", fontSize: 28 }}>✓</div>
          <p style={{ color: c.gold, fontSize: 11, letterSpacing: 4, textTransform: "uppercase", marginBottom: 12 }}>{t.brand}</p>
          <h1 style={{ color: c.text, fontSize: 34, margin: "0 0 16px", lineHeight: 1.1 }}>{t.successTitle}</h1>
          <p style={{ color: c.soft, fontSize: 16, lineHeight: 1.8, marginBottom: 10 }}>{t.successBody}</p>
          <p style={{ color: c.muted, fontSize: 13, lineHeight: 1.7, marginBottom: 28 }}>{t.successSub}</p>
          <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, padding: "20px 24px" }}>
            <p style={{ color: c.gold, fontSize: 14, fontStyle: "italic", margin: 0 }}>{t.signed}</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Form ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: c.bg, fontFamily: "Georgia, serif", color: c.text, paddingBottom: 100 }}>

      {/* Sticky header */}
      <div style={{ position: "sticky", top: 0, zIndex: 50, background: "rgba(8,8,6,0.94)", backdropFilter: "blur(12px)", borderBottom: `1px solid ${c.border}` }}>
        <div style={{ maxWidth: 680, margin: "0 auto", padding: "14px 20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div>
              <p style={{ margin: 0, color: c.gold, fontSize: 10, letterSpacing: 4, textTransform: "uppercase", marginBottom: 4 }}>{t.brand}</p>
              <h1 style={{ margin: 0, color: c.text, fontSize: 20, fontWeight: 600 }}>{t.title}</h1>
            </div>
            <div style={{ display: "flex", alignItems: "center", border: `1px solid ${c.border}`, borderRadius: 30, overflow: "hidden" }}>
              <button onClick={() => setLang("en")} style={{ padding: "6px 14px", background: lang === "en" ? c.goldDark : "transparent", color: lang === "en" ? c.text : c.muted, border: "none", cursor: "pointer", fontSize: 11, letterSpacing: 1, fontFamily: "inherit", transition: "all 0.2s" }}>EN</button>
              <button onClick={() => setLang("es")} style={{ padding: "6px 14px", background: lang === "es" ? c.goldDark : "transparent", color: lang === "es" ? c.text : c.muted, border: "none", cursor: "pointer", fontSize: 11, letterSpacing: 1, fontFamily: "inherit", transition: "all 0.2s" }}>ES</button>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ color: c.muted, fontSize: 11, letterSpacing: 1 }}>{t.stepOf(step, TOTAL_STEPS)}</span>
            <span style={{ color: c.muted2, fontSize: 10 }}>{Math.round(progress)}%</span>
          </div>
          <div style={{ height: 3, background: c.border, borderRadius: 99, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${progress}%`, background: `linear-gradient(90deg, ${c.goldDark}, ${c.gold})`, borderRadius: 99, transition: "width 0.4s ease" }} />
          </div>
          <p style={{ margin: "7px 0 0", color: c.muted2, fontSize: 11, textAlign: "center" }}>{t.barNote}</p>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 680, margin: "0 auto", padding: "32px 20px 0" }}>

        {step === 1 && (
          <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 14, padding: "20px 22px", marginBottom: 28 }}>
            <p style={{ margin: 0, color: c.text, fontSize: 16, lineHeight: 1.5, marginBottom: 10 }}>{t.tagline}</p>
            <p style={{ margin: 0, color: c.muted, fontSize: 13, lineHeight: 1.7, borderTop: `1px solid ${c.border}`, paddingTop: 10 }}>{t.systemLine}</p>
            <div style={{ marginTop: 12, display: "inline-flex", alignItems: "center", gap: 7, background: `${c.goldDark}1A`, border: `1px solid ${c.goldDark}44`, borderRadius: 20, padding: "5px 12px" }}>
              <span style={{ color: c.gold, fontSize: 12 }}>⏱</span>
              <span style={{ color: c.gold, fontSize: 11, letterSpacing: 0.5 }}>{t.timeNote}</span>
            </div>
          </div>
        )}

        <div key={`${lang}-${step}-${fadeKey}`} style={{ animation: "fadeSlide 0.22s ease" }}>

          {step === 1 && (
            <>
              <SectionHeader title={t.sections[0].title} desc={t.sections[0].desc} icon="⚖️" />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 140px", gap: 12, marginBottom: 14 }}>
                <div>
                  <FieldLabel t={t}>{f.weight}</FieldLabel>
                  <input type="number" inputMode="decimal" value={form.avgWeeklyWeight} onChange={(e) => update("avgWeeklyWeight", e.target.value)} placeholder="178.4" style={inputBase} />
                  <HelperText>{f.weightHelper}</HelperText>
                </div>
                <div>
                  <FieldLabel t={t}>{f.unit}</FieldLabel>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {["lbs", "kg"].map((u) => (
                      <button key={u} onClick={() => update("unit", u)} style={{ padding: "10px 0", borderRadius: 9, border: `1px solid ${form.unit === u ? c.goldDark : c.border}`, background: form.unit === u ? `${c.goldDark}22` : "#0a0a07", color: form.unit === u ? c.goldLight : c.muted, cursor: "pointer", fontFamily: "inherit", fontSize: 14, fontWeight: form.unit === u ? 700 : 400, transition: "all 0.18s" }}>
                        {u === "lbs" ? f.lbs : f.kg}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <SectionHeader title={t.sections[1].title} desc={t.sections[1].desc} icon="📋" />
              <div style={{ marginBottom: 14 }}>
                <FieldLabel t={t}>{f.adherence}</FieldLabel>
                <div style={{ position: "relative" }}>
                  <input type="number" inputMode="numeric" value={form.adherencePercent} onChange={(e) => update("adherencePercent", Math.min(100, Math.max(0, Number(e.target.value.replace(/[^0-9]/g, "")))))} placeholder="80" min={0} max={100} style={{ ...inputBase, paddingRight: 40 }} />
                  <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", color: c.muted, fontSize: 15 }}>%</span>
                </div>
                <HelperText>{f.adherenceHelper}</HelperText>
              </div>
              <SliderField label={f.protein} value={form.proteinConsistency} onChange={(v) => update("proteinConsistency", v)} lowLabel={f.proteinLow} highLabel={f.proteinHigh} />
            </>
          )}

          {step === 3 && (
            <>
              <SectionHeader title={t.sections[2].title} desc={t.sections[2].desc} icon="🔋" />
              <SliderField label={f.energy} value={form.energy} onChange={(v) => update("energy", v)} lowLabel={f.energyLow} highLabel={f.energyHigh} />
              <SliderField label={f.hunger} value={form.hunger} onChange={(v) => update("hunger", v)} lowLabel={f.hungerLow} highLabel={f.hungerHigh} />
              <SliderField label={f.sleep} value={form.sleepQuality} onChange={(v) => update("sleepQuality", v)} lowLabel={f.sleepLow} highLabel={f.sleepHigh} />
              <div style={{ background: "#0a0a07", border: `1px solid ${c.border}`, borderRadius: 12, padding: 18 }}>
                <label style={{ display: "block", color: c.soft, fontSize: 12, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 14, fontWeight: 600 }}>{f.digestion}</label>
                <RadioGroup value={form.digestion} onChange={(v) => update("digestion", v)} options={[{ value: "good", label: f.good }, { value: "neutral", label: f.neutral }, { value: "poor", label: f.poor }]} />
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <SectionHeader title={t.sections[3].title} desc={t.sections[3].desc} icon="🥊" />
              <div style={{ background: "#0a0a07", border: `1px solid ${c.border}`, borderRadius: 12, padding: 18, marginBottom: 14 }}>
                <label style={{ display: "block", color: c.soft, fontSize: 12, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 14, fontWeight: 600 }}>{f.training}</label>
                <RadioGroup value={form.trainingPerformance} onChange={(v) => update("trainingPerformance", v)} options={[{ value: "improving", label: f.improving }, { value: "stable", label: f.stable }, { value: "declining", label: f.declining }]} />
              </div>
              <div style={{ background: "#0a0a07", border: `2px solid ${c.goldDark}66`, borderRadius: 12, padding: 18, marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <label style={{ color: c.soft, fontSize: 12, letterSpacing: 1.5, textTransform: "uppercase", fontWeight: 600 }}>{f.weekendQ}</label>
                  <span style={{ background: `${c.goldDark}22`, border: `1px solid ${c.goldDark}44`, borderRadius: 20, padding: "2px 9px", color: c.gold, fontSize: 10, letterSpacing: 0.5 }}>⚑ Key signal</span>
                </div>
                <p style={{ color: c.muted2, fontSize: 11, margin: "0 0 14px", fontStyle: "italic" }}>{f.weekendHelper}</p>
                <RadioGroup value={form.weekendCompliance} onChange={(v) => update("weekendCompliance", v)} options={[{ value: "yes", label: f.yes }, { value: "no", label: f.no }]} />
              </div>
              {form.weekendCompliance === "no" && (
                <div style={{ animation: "fadeSlide 0.2s ease" }}>
                  <FieldLabel optional t={t}>{f.weekendDetail}</FieldLabel>
                  <textarea value={form.weekendDetail} onChange={(e) => update("weekendDetail", e.target.value)} placeholder={f.weekendPlaceholder} rows={3} style={{ ...inputBase, resize: "vertical", lineHeight: 1.7 }} />
                  <HelperText>{f.weekendDetailHelper}</HelperText>
                </div>
              )}
            </>
          )}

          {step === 5 && (
            <>
              <SectionHeader title={t.sections[4].title} desc={t.sections[4].desc} icon="💭" />
              <div style={{ marginBottom: 20 }}>
                <FieldLabel t={t}>{f.struggle}</FieldLabel>
                <textarea value={form.biggestStruggle} onChange={(e) => update("biggestStruggle", e.target.value)} placeholder={f.strugglePlaceholder} rows={4} style={{ ...inputBase, resize: "vertical", lineHeight: 1.7 }} />
              </div>
              <div>
                <FieldLabel t={t}>{f.win}</FieldLabel>
                <textarea value={form.biggestWin} onChange={(e) => update("biggestWin", e.target.value)} placeholder={f.winPlaceholder} rows={4} style={{ ...inputBase, resize: "vertical", lineHeight: 1.7 }} />
                <HelperText>{f.winHelper}</HelperText>
              </div>
            </>
          )}

          {step === 6 && (
            <>
              <SectionHeader title={t.sections[5].title} desc={t.sections[5].desc} icon="📊" />
              <div style={{ display: "grid", gap: 14, marginBottom: 20 }}>
                <ReviewCard icon="⚖️" title={t.sections[0].title} rows={[
                  { label: t.reviewLabels.weight, value: `${form.avgWeeklyWeight || "—"} ${form.unit}` },
                  { label: t.reviewLabels.adherence, value: form.adherencePercent ? `${form.adherencePercent}%` : "—" },
                  { label: t.reviewLabels.protein, value: `${form.proteinConsistency}/10` },
                ]} />
                <ReviewCard icon="🔋" title={t.sections[2].title} rows={[
                  { label: t.reviewLabels.energy, value: `${form.energy}/10` },
                  { label: t.reviewLabels.hunger, value: `${form.hunger}/10` },
                  { label: t.reviewLabels.sleep, value: `${form.sleepQuality}/10` },
                  { label: t.reviewLabels.digestion, value: form.digestion },
                ]} />
                <ReviewCard icon="🥊" title={t.sections[3].title} rows={[
                  { label: t.reviewLabels.training, value: form.trainingPerformance },
                  { label: t.reviewLabels.weekend, value: form.weekendCompliance },
                  ...(form.weekendDetail ? [{ label: t.reviewLabels.weekendDetail, value: form.weekendDetail }] : []),
                ]} />
                <ReviewCard icon="💭" title={t.sections[4].title} rows={[
                  { label: t.reviewLabels.struggle, value: form.biggestStruggle },
                  { label: t.reviewLabels.win, value: form.biggestWin },
                ]} />
              </div>
              <div style={{ textAlign: "center", padding: "18px 20px", background: `${c.goldDark}12`, border: `1px solid ${c.goldDark}33`, borderRadius: 12, marginBottom: 6 }}>
                <p style={{ margin: 0, color: c.soft, fontSize: 14, fontStyle: "italic", lineHeight: 1.7 }}>{t.reassurance}</p>
              </div>
              {status === "error" && (
                <div style={{ marginTop: 14, background: "#1a0505", border: `1px solid ${c.danger}`, borderRadius: 10, padding: "12px 16px" }}>
                  <p style={{ color: c.danger, fontSize: 13, margin: 0 }}>⚠ Something went wrong. Please try again.</p>
                </div>
              )}
            </>
          )}

        </div>
      </div>

      {/* Sticky bottom CTA */}
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 50, background: "rgba(8,8,6,0.97)", backdropFilter: "blur(12px)", borderTop: `1px solid ${c.border}`, padding: "14px 20px" }}>
        <div style={{ maxWidth: 680, margin: "0 auto", display: "flex", gap: 12, alignItems: "center" }}>
          <button onClick={goBack} disabled={step === 1} style={{ flex: "0 0 auto", padding: "14px 22px", borderRadius: 10, border: `1px solid ${step === 1 ? c.muted2 : c.border}`, background: "transparent", color: step === 1 ? c.muted2 : c.muted, fontFamily: "inherit", fontSize: 14, cursor: step === 1 ? "not-allowed" : "pointer", transition: "all 0.18s", minWidth: 80, textAlign: "center" }}>
            ← {t.back}
          </button>
          {step < TOTAL_STEPS ? (
            <button onClick={goNext} disabled={!isValid[step]} style={{ flex: 1, padding: "15px 24px", borderRadius: 10, border: "none", background: isValid[step] ? `linear-gradient(135deg, ${c.goldDark}, ${c.gold}, ${c.goldLight})` : `${c.goldDark}33`, color: isValid[step] ? "#000" : c.muted2, fontFamily: "inherit", fontSize: 14, fontWeight: 800, letterSpacing: 2, textTransform: "uppercase", cursor: isValid[step] ? "pointer" : "not-allowed", transition: "all 0.2s" }}>
              {step === TOTAL_STEPS - 1 ? t.review : t.next} →
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={status === "sending"} style={{ flex: 1, padding: "15px 24px", borderRadius: 10, border: "none", background: status === "sending" ? `${c.goldDark}55` : `linear-gradient(135deg, ${c.goldDark}, ${c.gold}, ${c.goldLight})`, color: status === "sending" ? c.muted : "#000", fontFamily: "inherit", fontSize: 14, fontWeight: 800, letterSpacing: 2, textTransform: "uppercase", cursor: status === "sending" ? "not-allowed" : "pointer", transition: "all 0.2s" }}>
              {status === "sending" ? "Sending..." : t.submit}
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeSlide { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        input[type=range]::-webkit-slider-thumb { cursor: pointer; }
        input[type=number]::-webkit-inner-spin-button { opacity: 0; }
        textarea, input { transition: border-color 0.2s; }
        textarea:focus, input:focus { border-color: ${c.goldDark} !important; }
      `}</style>
    </div>
  );
}
